import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { io } from "socket.io-client";
import { fetchTicketingConfig } from "../../redux/ticketingSlice";
import { landingCategoryImages } from "../../assets/landing-categories";
import ResponsiveCategoryImage from "../ResponsiveCategoryImage";

// Landing-page quick tile for the ticketing vertical. Renders only when the
// pilot flag is on for this market — so toggling ticketing off hides it here too.
// Styling matches the sibling QuickCategoryCard tiles in LandingPage.
const EventsTile = () => {
  const dispatch = useDispatch();
  const enabled = useSelector((s) => s.ticketing?.enabled);
  const configLoaded = useSelector((s) => s.ticketing?.configLoaded);

  useEffect(() => {
    if (!configLoaded) dispatch(fetchTicketingConfig());
  }, [configLoaded, dispatch]);

  // Kill-switch parity with mobile: when ops flip ticketing on/off the backend
  // emits `ticketing:update` type:"config" — re-fetch so the tile appears or
  // disappears without a page refresh.
  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      transports: ["websocket", "polling"],
    });
    socket.on("connect", () => socket.emit("join", "ticketing"));
    socket.on("ticketing:update", (msg) => {
      if (msg && msg.type === "config") dispatch(fetchTicketingConfig());
    });
    return () => {
      socket.off("ticketing:update");
      socket.disconnect();
    };
  }, [dispatch]);

  if (!enabled) return null;

  return (
    <Card to="/events">
      <IconWrap>
        <TileImage
          sources={landingCategoryImages.events}
          alt="Events & Tickets"
        />
      </IconWrap>
      <Title>Events &amp; Tickets</Title>
      <Subtitle>Book seats →</Subtitle>
    </Card>
  );
};

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-decoration: none;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    border-color: #83b524;
  }
`;
const IconWrap = styled.div`
  height: 133px;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 768px) {
    height: 56px;
  }
`;
// Mirrors LandingPage's QuickCategoryImage so this tile sits flush with its
// siblings (Food, Groceries, Healthcare, Package).
const TileImage = styled(ResponsiveCategoryImage)`
  width: 200px;
  max-height: 133px;
  object-fit: contain;
  display: block;
  margin-bottom: -8px;

  @media (max-width: 768px) {
    width: auto;
    height: 56px;
    margin-bottom: 0;
  }
`;
const Title = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  text-align: center;
  line-height: 1.3;
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;
const Subtitle = styled.span`
  font-size: 11px;
  color: #83b524;
  margin-top: 4px;
  font-weight: 500;
`;

export default EventsTile;
